<template>
  <!-- 상담 신청 다이얼로그 -->
  <v-dialog v-model="dialog"
            :width="isMobile ? undefined : '600px'"
            :fullscreen="isMobile"
            :hide-overlay="isMobile"
            :transition="isMobile ? 'dialog-bottom-transition' : undefined"
            :persistent="isMobile"
  >
    <v-card>
      <v-toolbar flat>
        <v-btn icon @click.stop="dialog = false">
          <v-icon>$vuetify.icons.cancel</v-icon>
        </v-btn>
        <h3 class="display-2" style="margin-bottom: 2px">상담 신청</h3>
      </v-toolbar>

      <v-form v-model="visitMeta.valid">
        <v-layout row wrap pa-5 mt-5 class="text-xs-left visit-form">
          <v-flex xs12 mb-5 class="text-xs-center">
            <h2>상담을 위해 필요한<br>정보를 입력해주세요.</h2>
          </v-flex>

          <v-flex xs12 mb-4>
            <h3>* 상담 진행 방식을 선택해주세요.</h3>
            <v-radio-group row v-model="visitInfo.isCall" hide-details class="mt-2">
              <v-radio on-icon="$vuetify.icons.confirm"
                       off-icon="$vuetify.icons.confirm"
                       color="primary"
                       value="visit">
                <template v-slot:label>
                  <span :class="{ 'primary--text': visitInfo.isCall === 'visit' }">방문상담</span>
                </template>
              </v-radio>
              <v-radio on-icon="$vuetify.icons.confirm"
                       off-icon="$vuetify.icons.confirm"
                       color="primary"
                       value="phone">
                <template v-slot:label>
                  <span :class="{ 'primary--text': visitInfo.isCall === 'phone' }">전화상담</span>
                </template>
              </v-radio>
              <v-radio on-icon="$vuetify.icons.confirm"
                       off-icon="$vuetify.icons.confirm"
                       color="primary"
                       value="kakaotalk">
                <template v-slot:label>
                  <span :class="{ 'primary--text': visitInfo.isCall === 'kakaotalk' }">카톡상담</span>
                </template>
              </v-radio>
            </v-radio-group>
          </v-flex>

          <v-flex xs12>
            <h3 class="mb-3">* 필수 정보를 입력해주세요.</h3>
            <v-text-field label="이름"
                          placeholder="이름"
                          v-model="visitInfo.name"
                          :rules="[rules.required]"
                          required>
            </v-text-field>
          </v-flex>
          <v-flex xs12>
            <v-text-field label="휴대폰 번호"
                          placeholder="000-0000-0000"
                          :rules="[rules.required]"
                          :mask="phoneMask"
                          v-model="visitInfo.phone"
                          required></v-text-field>
          </v-flex>
          <v-flex xs12 mb-4>
            <v-text-field label="생년월일(6자리)"
                          placeholder="예시: 010523"
                          :rules="[rules.required]"
                          mask="######"
                          v-model="visitInfo.birthday"></v-text-field>
          </v-flex>

          <v-flex xs12 mb-4>
            <h3>* 관심있는 국가를 선택해주세요.</h3>
            <span class="caption-form">중복선택 가능</span>
            <v-select append-icon="$vuetify.icons.arrowDown"
                      ref="nationSelect"
                      @click:append.stop="toggleSelectMenu($refs.nationSelect)"
                      :menu-props="{ 'offsetY': true, 'openOnClick': true, 'closeOnClick': false }"
                      placeholder="관심 국가"
                      v-model="visitInfo.interestNation"
                      required
                      :rules="[rules.requiredMultiple]"
                      multiple
                      :items="visitMeta.nations">
              <template v-slot:item="{ item }">
                {{ item }}
              </template>
            </v-select>
          </v-flex>

          <v-flex xs12 mb-4>
            <h3>* 희망하는 유학 기간을 선택해주세요.</h3>
            <v-select append-icon="$vuetify.icons.arrowDown"
                      ref="periodSelect"
                      @click:append.stop="toggleSelectMenu($refs.periodSelect)"
                      :menu-props="{ 'offsetY': true, 'openOnClick': true, 'closeOnClick': false }"
                      placeholder="희망 기간"
                      required
                      v-model="visitInfo.studyPeriod"
                      :rules="[rules.requiredMultiple]"
                      :items="visitMeta.durations">
              <template v-slot:item="{ item }">
                {{ item }}
              </template>
            </v-select>
          </v-flex>

          <v-flex xs12 mb-4>
            <h3>* 희망하는 유학 시작 시기를 선택해주세요.</h3>
            <v-date-picker no-title
                           type="month"
                           v-model="visitInfo.studyStartDate"
                           class="flat-calendar"
                           full-width
                           next-icon="$vuetify.icons.arrowRight"
                           prev-icon="$vuetify.icons.arrowLeft"
                           locale="ko"
                           color="#ec514f"
                           :min="minDate"></v-date-picker>
          </v-flex>

          <v-flex xs12 mb-4>
            <h3>* 상담 날짜 및 시간을 선택해주세요.</h3>
            <span class="mb-2 caption-form">월~금 11시~19시 (공휴일 제외)</span>
            <v-date-picker no-title
                           class="flat-calendar"
                           v-model="visitInfo.date"
                           full-width
                           next-icon="$vuetify.icons.arrowRight"
                           prev-icon="$vuetify.icons.arrowLeft"
                           locale="ko"
                           color="#ec514f"
                           :min="minDate"
                           :max="maxDate"
                           :allowed-dates="date => allowedDates(date)"
                           :day-format="date => date.split('-')[2]">
            </v-date-picker>
            <div class="caption" style="color: #ff5252" v-if="!visitInfo.date">필수사항입니다.</div>
            <v-select append-icon="$vuetify.icons.arrowDown"
                      label="상담 시간"
                      ref="timeSelect"
                      @click:append.stop="toggleSelectMenu($refs.timeSelect)"
                      :menu-props="{ 'offsetY': true, 'offsetOverflow': true, 'openOnClick': true, 'closeOnClick': false }"
                      required
                      class="mt-3"
                      v-model="visitInfo.time"
                      :rules="[rules.required]"
                      :items="visitMeta.times"
                      item-disabled="subheader">
              <template v-slot:item="{ item }" :disabled="item.subheader">
                <v-subheader v-if="item.subheader">{{ item.text }}</v-subheader>
                <div v-else>{{ item.text }}</div>
              </template>
            </v-select>
          </v-flex>

          <v-flex xs12 mb-4>
            <h3>궁금하신 점이 있으면 작성해주세요 :)</h3>
            <v-textarea placeholder="문의사항" v-model="visitInfo.question" no-resize counter="500"
                        :rules="[v => v.length <= 200 || '500자를 초과하는 부분은 제외됩니다.']"
            ></v-textarea>
          </v-flex>

          <v-flex xs12 v-show="visitInfo.isCall === 'visit'">
            <h3>방문상담 위치를 확인해주세요.</h3>
            <!--                <span class="mb-2 caption-form">{{ visitInfo.location }}</span>-->
            <!--                <v-select disabled label="상담 장소" :items="[ '서울 역삼점' ]"></v-select>-->
            <v-text-field readonly label="상담 장소" v-model="visitInfo.location"></v-text-field>
          </v-flex>

          <v-flex xs12 mb-4 v-show="visitInfo.isCall === 'visit'">
            <div id="map" style="height: 300px;"></div>
          </v-flex>

          <v-flex xs12 mb-2>
            <h3>어떻게 유스를 알게 되었나요?</h3>
            <span class="mb-2 caption-form">선택사항</span>
            <v-radio-group row v-model="visitInfo.funnel" hide-details class="mt-2">
              <template v-for="(item, index) of visitMeta.funnels">
                <v-radio :key="index"
                         :value="item.value"
                         on-icon="$vuetify.icons.confirm"
                         off-icon="$vuetify.icons.confirm"
                         color="primary"
                         class="mr-5 mb-2">
                  <template v-slot:label>
                    <span :class="{ 'primary--text': visitInfo.funnel === item.value }">{{ item.label }}</span>
                  </template>
                </v-radio>
              </template>
            </v-radio-group>
            <v-text-field v-if="visitInfo.funnel === 'other'"
                          label="기타"
                          placeholder="유튜브, 지인소개, ..."
                          v-model="visitInfo.funnelOther">
            </v-text-field>
          </v-flex>

          <v-flex xs12>
            <v-expansion-panel class="expansion-panel-form">
              <v-expansion-panel-content>
                <template v-slot:actions>
                  <v-icon>$vuetify.icons.arrowDown</v-icon>
                </template>
                <template v-slot:header>
                  <v-checkbox on-icon="$vuetify.icons.confirm"
                              off-icon="$vuetify.icons.confirm"
                              color="primary"
                              class="pt-0"
                              style="margin-top: 10px;"
                              required
                              :rules="[rules.required]"
                              :ripple="false"
                              @click.stop
                              v-model="visitInfo.agreePrivacy">
                    <template v-slot:label>
                      <div style="font-size: 16px;">
                        <span>{{ privacyTerm.title }} 동의</span>
                        <span class="caption-form">&nbsp;[필수]</span>
                      </div>
                    </template>
                  </v-checkbox>
                </template>
                <div v-html="privacyTerm.content" class="pa-3" style="height: 400px; overflow-y: scroll; border: 1px solid #f4f4f4; border-radius: 10px;"></div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-flex>
          <v-flex xs12>
            <v-btn color="#ec514f" class="mt-5" style="height: 56px; color: white;"
                   depressed
                   :disabled="!visitMeta.valid || !visitInfo.date"
                   :loading="submitLoading"
                   block @click="submit">신청하기</v-btn>
          </v-flex>
        </v-layout>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import terms from '@/assets/js/terms';

  export default {
    name: 'VisitDialog',
    props: {
      isMobile: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      ...mapGetters([ 'visitDialog', 'timeInvalid' ]),
      ...mapGetters('user', { userIdx: 'idx' }),
      dialog: {
        get () {
          return this.visitDialog;
        },
        set (flag) {
          this.toggleVisitDialog(flag);
        }
      },
      minDate () {
        const offset = new Date().getTimezoneOffset();
        return new Date(new Date().getTime() - (offset * 60000)).toISOString().split('T')[0];
      },
      maxDate () {
        const offset = new Date().getTimezoneOffset();
        return new Date(new Date().getTime() - (offset * 60000) + (1000 * 60 * 60 * 24 * 31)).toISOString().split('T')[0];
      },
      privacyTerm () {
        return terms.visitPrivacy;
      },
      phoneMask () {
        return this.visitInfo.phone.value && this.visitInfo.phone.value.length <= 10 ? '###-###-#####' : '###-####-####';
      },
    },
    data () {
      return {
        submitLoading: false,
        isOpenNationMenu: false,
        visitMeta: {
          valid: false,
          nations: [ '미국', '캐나다', '영국', '호주', '뉴질랜드', '아일랜드', '몰타' ],
          times: [
            { text: '오전', subheader: true },
            { text: '오전 11시 ~ 낮 12시', value: '11:00' },
            { text: '오후', subheader: true },
            { text: '낮 12시 ~ 1시', value: '12:00' },
            { text: '오후 1시 ~ 2시', value: '13:00' },
            { text: '오후 2시 ~ 3시', value: '14:00' },
            { text: '오후 3시 ~ 4시', value: '15:00' },
            { text: '오후 4시 ~ 5시', value: '16:00' },
            { text: '오후 5시 ~ 6시', value: '17:00' },
            { text: '오후 6시 ~ 7시', value: '18:00' },
            { text: '오후 7시 ~ 8시', value: '19:00' },
          ],
          durations: [ '4주 이내', '3개월 이내', '6개월 이내', '1년 미만', '1년 이상'],
          funnels: [
            { label: '네이버', value: 'naver' },
            { label: '페이스북', value: 'facebook' },
            { label: '인스타그램', value: 'instagram' },
            { label: '기타', value: 'other' },
          ]
        },
        rules: {
          required: v => !!v || '필수사항입니다.',
          requiredMultiple: v => !!v.length || '필수사항입니다.'
        },
        visitInfo: {
          isCall: 'visit',
          name: '',
          phone: '',
          birthday: '',
          interestNation: [],
          studyPeriod: '',
          studyStartDate: '',
          question: '',
          date: '',
          time: '',
          location: '서울 역삼점 (서울시 강남구 역삼로 180, 마루180)',
          agreePrivacy: true,
          funnel: '',
          funnelOther: '',
        },
      };
    },
    watch: {
      dialog (v) {
        if (v) {
          if (!this.timeInvalid.length) {
            this.selectTimeInvalid();
          }
          this.$nextTick(() => {
            /* eslint-disable */
            let mapContainer = document.getElementById('map'); // 지도를 표시할 div
            let mapOption = {
              center: new kakao.maps.LatLng(37.496522, 127.038803), // 지도의 중심좌표
              level: 5, // 지도의 확대 레벨
            };
            // 지도를 생성한다
            let kakaoMap = new kakao.maps.Map(mapContainer, mapOption);
            // 지도에 마커를 생성하고 표시한다
            let marker = new kakao.maps.Marker({
              position: new kakao.maps.LatLng(37.495522, 127.038803), // 마커의 좌표
              map: kakaoMap // 마커를 표시할 지도 객체
            });
            // 마커에 최대 너비 제한 있는지 자꾸 두줄 됨
            // 커스텀 오버레이를 생성하고 지도에 표시한다
            let customOverlay = new kakao.maps.CustomOverlay({
              map: kakaoMap,
              content: '<div style="padding:0 5px;background:#fff;border:1px solid #4a4a4a;">서울시 강남구 역삼로 180 (마루180)</div>',
              position: new kakao.maps.LatLng(37.495522, 127.038803), // 커스텀 오버레이를 표시할 좌표
              xAnchor: 0.5, // 컨텐츠의 x 위치
              yAnchor: 3 // 컨텐츠의 y 위치
            });
            /* eslint-enable */
          });
        }
      }
    },
    methods: {
      ...mapActions([ 'toggleVisitDialog', 'selectTimeInvalid' ]),
      ...mapActions('product', [ 'createConsultingForm' ]),
      toggleSelectMenu (refs) {
        // console.log(refs);
        if (refs.isMenuActive) {
          refs.blur();
        } else {
          refs.focus();
          refs.activateMenu();
        }
      },
      submit () {
        this.submitLoading = true;
        this.createConsultingForm({
          storeIdx: this.idx ? this.idx : parseInt(this.$route.params.idx),
          type: this.visitInfo.isCall,
          location: this.visitInfo.isCall === 'visit' ? '서울 역삼점' : this.visitInfo.isCall === 'phone' ? '전화' : '카톡',
          name: this.visitInfo.name,
          phone: this.visitInfo.phone,
          birthday: this.visitInfo.birthday,
          channel: this.visitInfo.funnel === 'other' ? this.visitInfo.funnelOther : this.visitInfo.funnel,
          interestNation: this.visitInfo.interestNation.join(','),
          studyPeriod: this.visitInfo.studyPeriod,
          studyStartDate: this.visitInfo.studyStartDate,
          question: this.visitInfo.question,
          schedule: (new Date(`${this.visitInfo.date}T${this.visitInfo.time}:00+09:00`)).getTime(),
        }).then(response => {
          console.log(response);
          if (this.visitInfo.isCall === 'kakaotalk') {
            if (confirm('상담이 신청되었습니다 👏 카카오톡 채팅으로 바로 연결할까요?')) {
              // eslint-disable-next-line
              Kakao.PlusFriend.chat({
                plusFriendId: '_ygxjIT'
              });
            }
          } else {
            alert('상담이 신청되었습니다 👏 상담 신청 내용은 카카오톡으로 안내드릴 예정입니다. 성공적인 유학을 위해 유스가 도와드릴게요! 😊');
          }
          this.submitLoading = false;
          this.dialog = false;
        }).catch(err => {
          console.error(err);
          this.submitLoading = false;
        });
      },
      allowedDates (date) {
        if (this.timeInvalid.includes(date)) {
          return false;
        }
        return new Date(date).getDay() % 6 !== 0;
        // switch (date) {
        //   case '2019-12-25':
        //     return false;
        //   default:
        //     return new Date(date).getDay() % 6 !== 0;
        // }
      }
    }
  }
</script>

<style lang="scss" scoped>
  ::v-deep.visit-form {
    .v-input__slot::before {
      border-color: #f4f4f4 !important;
    }
  }
  ::v-deep.flat-calendar {
    box-shadow: none;
    .v-date-picker-table {
      padding: 0;
      height: auto;
    }
  }
  ::v-deep.expansion-panel-form {
    box-shadow: none;
    .v-expansion-panel__header {
      padding: 12px 0;
    }
  }
  ::v-deep.v-input--checkbox {
    .v-input__slot {
      margin-bottom: 0 !important;
    }
    .v-messages {
      min-height: 12px;
    }
    i {
      margin: auto;
      font-size: 16px;
    }
    input[aria-checked=false] ~ i {
      color: #cdcdcd !important;
    }
  }
</style>
